import { useCallback } from 'react';
import type { QuickFixConfig } from '../quickFixConfig';
import type { ValidationIssue } from '../types';
import {
  useValidationQuickFixExecution,
  type ValidationQuickFixPayload,
} from './useValidationQuickFixExecution';
import type { RevitOperationProgressSnapshot } from '../../../shared/api/grpc/operationProgressClient';
import {
  usePcfValidationStore,
  type ValidationQuickFixOperation,
  type ValidationQuickFixTransaction,
} from '../../../shared/stores/usePcfValidationStore';
import { useQuickFillStore } from '../../../shared/stores/useQuickFillStore';

export interface ValidationQuickFixPlan {
  title: string;
  description: string;
  affectedCount: number;
  affectedElements: string[];
  currentValue: string;
  proposedValue: string;
  undoAvailable: boolean;
  operations: ValidationQuickFixOperation[];
  undoOperations: ValidationQuickFixOperation[];
}

export interface ValidationQuickFixExecutionSummary {
  fixedCount: number;
  failedCount: number;
  totalCount: number;
  failureMessages: string[];
  affectedValidationIds: number[];
}

interface BuildQuickFixPlanArgs {
  issue: ValidationIssue;
  fix: QuickFixConfig;
  resolveRevitParam: (parameterName: string) => string;
  inputValue?: string;
  dualInputValues?: Record<string, string>;
  elementIdsOverride?: string[];
  affectedElementsOverride?: string[];
}

interface ApplyQuickFixPlanArgs {
  plan: ValidationQuickFixPlan;
  familyId?: string | null;
  itemId?: string | null;
  snapshotLabel?: string;
  onProgress?: (progress: RevitOperationProgressSnapshot) => void;
}

interface UndoQuickFixTransactionOptions {
  onProgress?: (progress: RevitOperationProgressSnapshot) => void;
}

const EMPTY_RESTORE_TOKENS = new Set(['', '<empty>', '(empty)']);

function normalizeRestoreValue(value: string | undefined): string {
  const trimmed = (value || '').trim();
  if (!trimmed) {
    return '';
  }

  return EMPTY_RESTORE_TOKENS.has(trimmed.toLowerCase()) ? '' : trimmed;
}

function buildUndoOperation(
  operation: ValidationQuickFixOperation
): ValidationQuickFixOperation | null {
  if (operation.fixType === 'autoResolveEndTypes' || operation.fixType === 'assignRepairPipingSystem') {
    return null;
  }

  return {
    fixType: 'setParameter',
    parameterName: operation.parameterName,
    revitParameterName: operation.revitParameterName,
    value: normalizeRestoreValue(operation.beforeValue),
    elementIds: [...operation.elementIds],
    validationId: operation.validationId,
    beforeValue: operation.value,
    description: `Restore ${operation.parameterName} to the previous value`,
  };
}

function isQuickFixOperation(
  operation: ValidationQuickFixOperation | null
): operation is ValidationQuickFixOperation {
  return operation !== null;
}

export function buildValidationQuickFixPlan({
  issue,
  fix,
  resolveRevitParam,
  inputValue = '',
  dualInputValues = {},
  elementIdsOverride,
  affectedElementsOverride,
}: BuildQuickFixPlanArgs): ValidationQuickFixPlan {
  const resolveParameterName = (fallback: string) =>
    issue.AdditionalData?.parameterName || fallback;
  const currentValue = normalizeRestoreValue(issue.AdditionalData?.currentValue);
  const targetElementIds = elementIdsOverride && elementIdsOverride.length > 0
    ? elementIdsOverride
    : issue.ElementIds;
  const affectedElements =
    affectedElementsOverride && affectedElementsOverride.length > 0
      ? affectedElementsOverride
      : issue.AffectedElements.length > 0
        ? issue.AffectedElements
        : targetElementIds.map((elementId) => `Element ${elementId}`);

  const operations =
    fix.type === 'dualInput' && fix.fields
      ? fix.fields
          .map<ValidationQuickFixOperation | null>((field) => {
            const nextValue = (dualInputValues[field.parameterName] || '').trim();
            if (!nextValue) {
              return null;
            }

            const parameterName = resolveParameterName(field.parameterName);
            return {
              fixType: 'setParameter',
              parameterName,
              revitParameterName: resolveRevitParam(parameterName),
              value: nextValue,
              elementIds: [...targetElementIds],
              validationId: issue.ValidationId,
              beforeValue: currentValue,
              description: `${field.label}: ${field.placeholder}`,
            } satisfies ValidationQuickFixOperation;
          })
          .filter(isQuickFixOperation)
      : [
          {
            fixType: fix.fixType,
            parameterName: resolveParameterName(fix.parameterName),
            revitParameterName: resolveRevitParam(resolveParameterName(fix.parameterName)),
            value: fix.canAutoFix ? '' : inputValue.trim(),
            elementIds: [...targetElementIds],
            validationId: issue.ValidationId,
            beforeValue: currentValue,
            description: fix.description,
          } satisfies ValidationQuickFixOperation,
        ] satisfies ValidationQuickFixOperation[];

  const undoOperations = operations
    .map(buildUndoOperation)
    .filter(isQuickFixOperation);

  return {
    title: fix.label,
    description: fix.description,
    affectedCount: targetElementIds.length,
    affectedElements,
    currentValue,
    proposedValue:
      fix.canAutoFix
        ? fix.fixType === 'autoResolveEndTypes'
          ? 'Auto-resolve END-TYPE values'
          : fix.fixType === 'assignRepairPipingSystem'
            ? 'Assign PipeEX-RepairFlow system'
            : 'Auto-detect value'
        : fix.type === 'dualInput'
          ? operations.map((operation) => `${operation.parameterName}: ${operation.value}`).join(', ')
          : inputValue.trim(),
    undoAvailable: operations.length > 0 && undoOperations.length === operations.length,
    operations,
    undoOperations,
  };
}

interface QuickFixElementResult {
  status?: string;
  message?: string;
}

const MAX_FAILURE_MESSAGES = 3;

function collectFailureMessages(
  results: Array<{ results?: QuickFixElementResult[] }>
): string[] {
  const messages: string[] = [];
  for (const result of results) {
    for (const item of result.results ?? []) {
      const message = (item.message || '').trim();
      if (item.status === 'failed' && message && !messages.includes(message)) {
        messages.push(message);
      }
    }
  }

  return messages.slice(0, MAX_FAILURE_MESSAGES);
}

export function aggregateExecutionResult(
  results: Array<{ fixedCount?: number; failedCount?: number; totalCount?: number; results?: QuickFixElementResult[] }>,
  operations: Array<Pick<ValidationQuickFixOperation, 'validationId'>> = []
): ValidationQuickFixExecutionSummary {
  const counts = results.reduce<Pick<ValidationQuickFixExecutionSummary, 'fixedCount' | 'failedCount' | 'totalCount'>>(
    (aggregate, result) => ({
      fixedCount: aggregate.fixedCount + (result.fixedCount || 0),
      failedCount: aggregate.failedCount + (result.failedCount || 0),
      totalCount: aggregate.totalCount + (result.totalCount || 0),
    }),
    {
      fixedCount: 0,
      failedCount: 0,
      totalCount: 0,
    }
  );

  return {
    ...counts,
    failureMessages: collectFailureMessages(results),
    affectedValidationIds: Array.from(
      new Set(
        operations.flatMap((operation, index) =>
          (results[index]?.fixedCount ?? 0) > 0 ? [operation.validationId] : []
        )
      )
    ),
  };
}

export function useValidationQuickFixFlow() {
  const executeQuickFix = useValidationQuickFixExecution();
  const captureRollbackSnapshot = useQuickFillStore((state) => state.captureRollbackSnapshot);
  const restoreRollbackSnapshot = useQuickFillStore((state) => state.restoreRollbackSnapshot);
  const discardRollbackSnapshot = useQuickFillStore((state) => state.discardRollbackSnapshot);
  const addQuickFixTransaction = usePcfValidationStore((state) => state.addQuickFixTransaction);
  const markQuickFixTransactionUndone = usePcfValidationStore((state) => state.markQuickFixTransactionUndone);
  const markQuickFixTransactionUndoFailed = usePcfValidationStore((state) => state.markQuickFixTransactionUndoFailed);

  const runOperations = useCallback(
    async (
      operations: ValidationQuickFixOperation[],
      onProgress?: (progress: RevitOperationProgressSnapshot) => void
    ) => {
      const results: Array<{ fixedCount?: number; failedCount?: number; totalCount?: number }> = [];
      for (const operation of operations) {
        const result = await executeQuickFix(operation as ValidationQuickFixPayload, { onProgress });
        results.push(result || {});
      }
      return aggregateExecutionResult(results, operations);
    },
    [executeQuickFix]
  );

  const applyQuickFixPlan = useCallback(
    async ({
      plan,
      familyId = null,
      itemId = null,
      snapshotLabel,
      onProgress,
    }: ApplyQuickFixPlanArgs) => {
      const snapshotId = captureRollbackSnapshot(
        snapshotLabel || `${plan.title} rollback snapshot`
      );

      try {
        const result = await runOperations(plan.operations, onProgress);
        const transaction: ValidationQuickFixTransaction = {
          id: `validation-quick-fix-${Date.now()}-${Math.round(Math.random() * 1000)}`,
          familyId,
          itemId,
          title: plan.title,
          description: plan.description,
          createdAt: new Date().toISOString(),
          affectedCount: plan.affectedCount,
          status: 'applied',
          undoAvailable: plan.undoAvailable,
          snapshotId,
          forwardOperations: plan.operations,
          undoOperations: plan.undoOperations,
        };

        addQuickFixTransaction(transaction);

        return {
          transaction,
          result,
        };
      } catch (error) {
        discardRollbackSnapshot(snapshotId);
        throw error;
      }
    },
    [
      addQuickFixTransaction,
      captureRollbackSnapshot,
      discardRollbackSnapshot,
      runOperations,
    ]
  );

  const undoQuickFixTransaction = useCallback(
    async (
      transaction: ValidationQuickFixTransaction,
      options: UndoQuickFixTransactionOptions = {}
    ) => {
      if (!transaction.undoAvailable || transaction.undoOperations.length === 0) {
        const error = 'Undo is not available for this quick fix.';
        markQuickFixTransactionUndoFailed(transaction.id, error);
        throw new Error(error);
      }

      try {
        const result = await runOperations(transaction.undoOperations, options.onProgress);
        if (transaction.snapshotId) {
          restoreRollbackSnapshot(transaction.snapshotId);
        }
        markQuickFixTransactionUndone(transaction.id);
        return result;
      } catch (error: any) {
        markQuickFixTransactionUndoFailed(
          transaction.id,
          error?.message || 'Undo failed'
        );
        throw error;
      }
    },
    [
      markQuickFixTransactionUndoFailed,
      markQuickFixTransactionUndone,
      restoreRollbackSnapshot,
      runOperations,
    ]
  );

  return {
    applyQuickFixPlan,
    undoQuickFixTransaction,
  };
}
